import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createLogger } from '../../utils/logger';
import {
  callbackOf,
  deref,
  jsonSchemaOf,
  operationsOf,
  sampleFromSchema,
  splitSections,
  type OpenApiSpec,
  type Operation,
  type SecurityScheme,
} from './openapi';
import {
  CodeBlock,
  JsonBlock,
  Markdown,
  MetaGrid,
  Note,
  SchemaTable,
  Section,
  SubHeading,
  type MetaItem,
} from './render';

const log = createLogger('ApiDocs');

/**
 * The integration contract of one sub-app: what a caller sends it, and what it
 * sends back to their callback URL.
 *
 * Everything here is read from that app's filtered OpenAPI spec — generated from
 * the same Pydantic models its handlers use — so a Docs page cannot drift from
 * the code the way a committed document would. The prose is the spec's own
 * description, split on its headings, for the same reason. Which means an app
 * gets its reference by serving a spec at {@link INTEGRATION_SPEC_PATH}; nothing
 * about the layout below is written per app.
 *
 * Laid out as one document rather than a set of tabs, matching gen-api's
 * reference: the topics on the left scroll to their section instead of swapping
 * it in, and every payload is on the page rather than behind a toggle, since a
 * reference is skimmed top to bottom at least as often as it is searched.
 */

/**
 * Where every sub-app serves the caller-facing subset of its OpenAPI spec.
 *
 * Under `/api` because that is the only prefix a deployed request survives: the
 * API Gateway forwards `/api/{path=**}` and nothing else, and a static host
 * serving this SPA proxies the same prefix. A path outside it comes back as the
 * gateway's error page or as `index.html` — either way, HTML where the page
 * expected a spec. Mirrors `shared.integration_docs.SPEC_PATH`.
 */
export const INTEGRATION_SPEC_PATH = '/api/integration-openapi.json';

export interface ApiDocsProps {
  /** The app's API base, as its own `*_API_BASE` constant reports it. */
  apiBase: string;
  /** The app's authenticated fetch helper. */
  fetchSpec: (path: string) => Promise<Response>;
}

/** Absolute, so the URL shown is the one a caller can actually POST to. */
function absoluteBase(apiBase: string): string {
  if (typeof window === 'undefined') return apiBase;
  try {
    return new URL(apiBase || '/', window.location.origin).toString().replace(/\/+$/, '');
  } catch {
    return apiBase;
  }
}

/** The header a scheme travels in. `http` schemes don't name one; they are always `Authorization`. */
function headerOf(scheme?: SecurityScheme): string | undefined {
  if (scheme?.type === 'http') return 'Authorization';
  return scheme?.name;
}

function describe(scheme?: SecurityScheme): string | undefined {
  if (scheme?.type === 'apiKey') return `API key, sent in the ${scheme.in ?? 'header'}`;
  if (scheme?.type === 'http' && scheme.scheme === 'bearer') {
    return `bearer token${scheme.bearerFormat ? `, ${scheme.bearerFormat}` : ''}`;
  }
  return scheme?.type;
}

/** A placeholder standing in for the credential itself, never the credential. */
function placeholderFor(scheme?: SecurityScheme): string {
  if (scheme?.type === 'http') return 'Bearer <google-id-token>';
  return '<your-api-key>';
}

interface SchemeEntry {
  name: string;
  scheme?: SecurityScheme;
}

/** Every scheme an operation demands, as the spec declares them. */
function schemesOf(spec: OpenApiSpec, operation: Operation): SchemeEntry[] {
  return (operation.security ?? [])
    .flatMap((requirement) => Object.keys(requirement))
    .map((name) => ({ name, scheme: spec.components?.securitySchemes?.[name] }))
    .filter((entry) => entry.scheme);
}

/**
 * A runnable `curl` for one operation.
 *
 * Path parameters are filled from the operation's own example where it names
 * them — a poll URL reading `/jobs/{job_id}` next to a response whose `job_id`
 * is a real UUID is a command that can be pasted and edited, rather than a
 * shape to be decoded.
 */
function buildCurl({
  method,
  url,
  schemes,
  body,
}: {
  method: string;
  url: string;
  schemes: SchemeEntry[];
  body?: unknown;
}): string {
  const lines = [`curl -X ${method.toUpperCase()} '${url}' \\`];
  if (body !== undefined) lines.push(`  -H 'Content-Type: application/json' \\`);
  for (const { scheme } of schemes) {
    lines.push(`  -H '${headerOf(scheme)}: ${placeholderFor(scheme)}' \\`);
  }
  if (body !== undefined) lines.push(`  -d '${JSON.stringify(body)}'`);

  // Whichever line ended up last carries a continuation it does not need.
  const last = lines.length - 1;
  lines[last] = lines[last].replace(/ \\$/, '');
  return lines.join('\n');
}

/** Fill `{param}` from a sample payload that happens to name it. */
function resolvePath(path: string, sample: unknown): string {
  const values = (sample ?? {}) as Record<string, unknown>;
  return path.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = values[name];
    return typeof value === 'string' || typeof value === 'number' ? String(value) : whole;
  });
}

/** The 2xx response of an operation, with its status code. */
function successOf(
  spec: OpenApiSpec,
  operation: Operation,
): { code: string; sample: unknown } | undefined {
  for (const [code, response] of Object.entries(operation.responses ?? {})) {
    if (!code.startsWith('2')) continue;
    const schema = jsonSchemaOf(response);
    if (schema) return { code, sample: sampleFromSchema(spec, schema) };
  }
  return undefined;
}

/**
 * One operation: what it takes, what it answers with, and a command to try it.
 *
 * `pathSample` is a payload to lift path-parameter values from — the submit's
 * response, for the poll that follows it.
 */
function OperationDoc({
  spec,
  base,
  method,
  path,
  operation,
  pathSample,
  requestLabel = 'Request',
}: {
  spec: OpenApiSpec;
  base: string;
  method: string;
  path: string;
  operation: Operation;
  pathSample?: unknown;
  requestLabel?: string;
}) {
  const requestSchema = jsonSchemaOf(operation.requestBody);
  const requestSample = requestSchema ? sampleFromSchema(spec, requestSchema) : undefined;
  const success = successOf(spec, operation);
  const schemes = schemesOf(spec, operation);
  const resolved = resolvePath(path, pathSample);
  const url = `${base}${resolved}`;

  const meta: MetaItem[] = [{ label: 'Method / path', value: `${method.toUpperCase()} ${path}` }];
  if (schemes.length) {
    meta.push({
      label: schemes.length > 1 ? 'Auth headers (both required)' : 'Auth header',
      value: schemes.map(({ name, scheme }) => (
        <div key={name}>
          {headerOf(scheme)} <span className="text-surface-400">— {describe(scheme)}</span>
        </div>
      )),
    });
  }
  if (requestSchema) meta.push({ label: 'Content-Type', value: 'application/json' });

  // The body's schema when there is one; otherwise the response is the subject,
  // and its schema is the table worth showing.
  const table = requestSchema ?? (success ? jsonSchemaOf(pickSuccess(operation)) : undefined);

  return (
    <>
      <MetaGrid items={meta} />
      {operation.description && (
        <div className="mt-3">
          <Markdown source={operation.description} />
        </div>
      )}
      {table && (
        <div className="mt-3">
          <SchemaTable spec={spec} schema={table} />
        </div>
      )}
      <CodeBlock
        label={requestLabel}
        lang="shell"
        code={buildCurl({ method, url, schemes, body: requestSample })}
      />
      {requestSample !== undefined && <JsonBlock label="Request body" value={requestSample} />}
      {success && <JsonBlock label={`Response (${success.code})`} value={success.sample} />}
    </>
  );
}

/** The response object of the first 2xx an operation declares. */
function pickSuccess(operation: Operation) {
  for (const [code, response] of Object.entries(operation.responses ?? {})) {
    if (code.startsWith('2')) return response;
  }
  return undefined;
}

interface Topic {
  id: string;
  label: string;
  content: ReactNode;
}

export function ApiDocs({ apiBase, fetchSpec }: ApiDocsProps) {
  const [spec, setSpec] = useState<OpenApiSpec | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchSpec(INTEGRATION_SPEC_PATH);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = (await response.json()) as OpenApiSpec;
        if (!cancelled) setSpec(body);
      } catch (e) {
        log.error(`Could not load the integration spec: ${e}`);
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchSpec]);

  const base = useMemo(() => absoluteBase(apiBase), [apiBase]);

  const topics = useMemo<Topic[]>(() => {
    if (!spec) return [];

    // Which operation plays which role is read off the spec rather than named
    // per app: the one carrying a callback is the submit, and a GET taking an id
    // is the poll. An app that adds a third operation to its integration surface
    // gets a topic for it without this file learning about it.
    const operations = operationsOf(spec);
    const submit =
      operations.find((entry) => entry.operation.callbacks) ??
      operations.find((entry) => entry.method === 'post');
    const poll = operations.find(
      (entry) => entry.method === 'get' && entry.path.includes('{') && entry !== submit,
    );
    const others = operations.filter((entry) => entry !== submit && entry !== poll);
    const callback = callbackOf(submit?.operation);
    // The submit's answer names the job the poll then asks about, so its ids fill
    // the poll's path rather than a placeholder standing in for them.
    const submitted = submit ? successOf(spec, submit.operation)?.sample : undefined;

    // The description's `###` sections. Some are placed deliberately below; the
    // rest become their own topics, so renaming a heading costs placement but
    // never loses the words.
    const sections = splitSections(spec.info?.description ?? '');
    const claimed = new Set<string>();
    const take = (match: RegExp): string | undefined => {
      const found = sections.find((s) => match.test(s.title) && !claimed.has(s.title));
      if (found) claimed.add(found.title);
      return found?.body;
    };
    const intro = sections.find((s) => !s.title)?.body;
    if (intro !== undefined) claimed.add('');
    const flow = take(/flow/i);
    const auth = take(/auth/i);
    const layers = take(/layers/i);
    const paths = take(/path/i);

    const authSchemes = Object.entries(spec.components?.securitySchemes ?? {});
    const list: Topic[] = [];

    list.push({
      id: 'overview',
      label: 'Overview',
      content: (
        <Section id="overview" title="Overview">
          {intro && <Markdown source={intro} />}
          <MetaGrid
            items={[
              { label: 'Base URL', value: base },
              { label: 'Content-Type', value: 'application/json' },
              ...(authSchemes.length
                ? [
                    {
                      label: 'Auth headers',
                      value: authSchemes
                        .map(([name, scheme]) => headerOf(scheme) ?? name)
                        .join(' + '),
                    },
                  ]
                : []),
              { label: 'Version', value: spec.info?.version ?? '—' },
            ]}
          />
          {flow && (
            <>
              <SubHeading>The flow</SubHeading>
              <Markdown source={flow} />
            </>
          )}
        </Section>
      ),
    });

    if (auth || authSchemes.length) {
      list.push({
        id: 'auth',
        label: 'Authentication',
        content: (
          <Section id="auth" title="Authentication">
            {auth && <Markdown source={auth} />}
            {authSchemes.length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-surface-200">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 text-surface-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Header</th>
                      <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Kind</th>
                      <th className="px-3 py-2 text-left font-medium">What it carries</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100">
                    {authSchemes.map(([name, scheme]) => (
                      <tr key={name}>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          <code className="font-mono text-primary-600">
                            {headerOf(scheme) ?? name}
                          </code>
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          <code className="font-mono text-surface-500">{describe(scheme)}</code>
                        </td>
                        <td className="px-3 py-2 align-top text-surface-600">
                          {scheme.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        ),
      });
    }

    if (submit) {
      // The nav entry says what the section's heading says — each app words its
      // own summary, and a topic list that renames them sends a reader looking
      // for a heading that is not there.
      const title = submit.operation.summary ?? 'Submit a request';
      list.push({
        id: 'submit',
        label: title,
        content: (
          <Section id="submit" title={title}>
            <OperationDoc
              spec={spec}
              base={base}
              method={submit.method}
              path={submit.path}
              operation={submit.operation}
            />
          </Section>
        ),
      });
    }

    if (callback) {
      const schema = jsonSchemaOf(callback.operation.requestBody);
      const resolvedSchema = schema ? deref(spec, schema) : undefined;
      list.push({
        id: 'response',
        label: 'Response',
        content: (
          <Section id="response" title="Response">
            {layers && <Markdown source={layers} />}
            <SubHeading>{callback.operation.summary ?? 'The callback'}</SubHeading>
            <MetaGrid
              items={[
                { label: 'Method / path', value: `POST ${callback.target}` },
                { label: 'Auth header', value: 'uveye-api-key' },
              ]}
            />
            {callback.operation.description && (
              <div className="mt-3">
                <Markdown source={callback.operation.description} />
              </div>
            )}
            {schema && (
              <div className="mt-3">
                <SchemaTable spec={spec} schema={schema} />
              </div>
            )}
            {resolvedSchema && (
              <JsonBlock
                label="Callback payload (POST to your callback_url)"
                value={sampleFromSchema(spec, resolvedSchema)}
              />
            )}
            {paths && (
              <>
                <SubHeading>Reading the paths in the result</SubHeading>
                <Markdown source={paths} />
              </>
            )}
          </Section>
        ),
      });
    }

    // The files the response only names. Straight after it, because a path in a
    // payload is where a reader first wonders what is in the file.
    for (const artifact of spec['x-artifacts'] ?? []) {
      const id = `artifact-${artifact.title.toLowerCase().replace(/\W+/g, '-')}`;
      list.push({
        id,
        label: artifact.title,
        content: (
          <Section id={id} title={artifact.title}>
            <MetaGrid
              items={[
                { label: 'Named by', value: artifact.namedBy },
                { label: 'Format', value: 'JSON' },
              ]}
            />
            <div className="mt-3">
              <Markdown source={artifact.description} />
            </div>
            <div className="mt-3">
              <SchemaTable spec={spec} schema={artifact.schema} />
            </div>
            <JsonBlock label="Example" value={sampleFromSchema(spec, artifact.schema)} />
          </Section>
        ),
      });
    }

    if (poll) {
      const title = poll.operation.summary ?? 'Poll for a result';
      list.push({
        id: 'poll',
        label: title,
        content: (
          <Section id="poll" title={title}>
            <OperationDoc
              spec={spec}
              base={base}
              method={poll.method}
              path={poll.path}
              operation={poll.operation}
              pathSample={submitted}
              requestLabel="Poll"
            />
            <Note>
              A run takes minutes rather than seconds, so poll every 5–15 seconds rather than in a
              tight loop.
            </Note>
          </Section>
        ),
      });
    }

    for (const entry of others) {
      const label = entry.operation.summary ?? `${entry.method.toUpperCase()} ${entry.path}`;
      const id = `${entry.method}-${entry.path}`.toLowerCase().replace(/\W+/g, '-');
      list.push({
        id,
        label,
        content: (
          <Section id={id} title={label}>
            <OperationDoc
              spec={spec}
              base={base}
              method={entry.method}
              path={entry.path}
              operation={entry.operation}
              pathSample={submitted}
            />
          </Section>
        ),
      });
    }

    // Anything in the description we did not place explicitly.
    for (const section of sections) {
      if (claimed.has(section.title) || !section.title) continue;
      const id = section.title.toLowerCase().replace(/\W+/g, '-');
      list.push({
        id,
        label: section.title,
        content: (
          <Section id={id} title={section.title}>
            <Markdown source={section.body} />
          </Section>
        ),
      });
    }

    return list;
  }, [spec, base]);

  // Highlight whichever section the reader is in: the last one whose heading has
  // passed the top of the viewport. Read off positions rather than watched with
  // an IntersectionObserver, because these sections are far taller than the
  // viewport — for most of a scroll no boundary is crossed at all, and the
  // highlight would sit wherever the last crossing left it.
  useEffect(() => {
    const root = container.current;
    if (!root || !topics.length) return;

    let queued = false;
    const measure = () => {
      queued = false;
      // A little below the sticky app header, so a heading counts as reached
      // once it is comfortably on screen rather than the instant it appears.
      const line = 120;
      let current = topics[0].id;
      for (const topic of topics) {
        const element = root.querySelector(`#${CSS.escape(topic.id)}`);
        if (element && element.getBoundingClientRect().top <= line) current = topic.id;
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(measure);
    };

    measure();
    // Captured, so a scroll of some container between us and the window counts too.
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [topics]);

  const jumpTo = (id: string) => {
    setActiveId(id);
    container.current
      ?.querySelector(`#${CSS.escape(id)}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-danger-200 p-6 shadow-soft text-sm text-danger-700">
        Could not load the API reference: {error}
      </div>
    );
  }

  if (!spec || !topics.length) {
    return (
      <div className="bg-white rounded-xl border border-surface-200 p-6 shadow-card flex items-center gap-2 text-sm text-surface-500">
        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Loading the API reference…
      </div>
    );
  }

  const active = activeId ?? topics[0].id;

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <nav className="md:w-52 md:shrink-0 md:order-first">
        <div className="md:sticky md:top-6 bg-white rounded-xl border border-surface-200 shadow-card p-2">
          {topics.map((topic) => (
            <button
              key={topic.id}
              onClick={() => jumpTo(topic.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                topic.id === active
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-surface-600 hover:bg-surface-50'
              }`}
            >
              {topic.label}
            </button>
          ))}
          <a
            href={`${apiBase}${INTEGRATION_SPEC_PATH}`}
            target="_blank"
            rel="noreferrer"
            className="block mt-1 px-3 py-2 rounded-lg text-xs text-surface-400 hover:bg-surface-50 hover:text-primary-600 transition-colors"
          >
            OpenAPI spec ↗
          </a>
        </div>
      </nav>

      <div ref={container} className="min-w-0 flex-1 max-w-4xl space-y-10 pb-24">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-surface-900">{spec.info?.title}</h2>
        </div>
        {topics.map((topic) => (
          <div key={topic.id}>{topic.content}</div>
        ))}
      </div>
    </div>
  );
}
